import { z } from 'zod';

import { creditPackIdSchema } from './billing.js';
import type { PlanTier } from './plans.js';

/**
 * DOĞRUDAN ÖDEME — kart formu bizim sitemizde, tahsilat iyzico API'siyle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden barındırılan ödeme sayfası (Checkout Form) değil — 22 Eyl 2026
 * ═══════════════════════════════════════════════════════════════════════════
 * Kullanıcı kararı: kart alanları panelin kendi formunda, iyzico sayfası da
 * iframe'i de yok. Kart bilgisi tarayıcıdan API'ye, API'den iyzico'ya gidiyor
 * ve HİÇBİR YERDE saklanmıyor (ne veritabanı, ne Redis, ne log). Saklanan tek
 * şey iyzico'nun verdiği kart jetonu.
 *
 * Bedeli PCI kapsamı: kart numarası sunucumuzdan geçtiği için SAQ A değil
 * SAQ D. Bu dosyadaki şemalar kart alanlarını doğruluyor ama hata
 * mesajlarına DEĞERİ koymuyor — Zod hatası log'a düşerse kart numarası
 * düşmesin.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Akış
 * ═══════════════════════════════════════════════════════════════════════════
 *   1. `POST /billing/pay/quote`  → ne ödenecek (oran indirimi dahil)
 *   2. `POST /billing/pay`        → deneme açılır, 3DS başlatılır
 *   3. tarayıcı bankanın 3DS sayfasına TAM SAYFA gider (iframe değil)
 *   4. banka → `POST /billing/pay/callback/:id/:nonce` → tahsilat → panele dönüş
 *   5. panel `GET /billing/pay/attempts/:id` ile sonucu okur
 *
 * Kullanıcının başlattığı HER ödeme 3D Secure. 3DS'siz tahsilat yalnız
 * zamanlanmış yenilemede ve yalnız 3DS'ten geçmiş (doğrulanmış) kartla.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Sabitler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * İKİ PARA BİRİMİ — 22 Eyl 2026.
 *
 * Katalog (plan ve paket fiyatları) USD. Kart ise TL olarak çekiliyor:
 * iyzico yerli kartla döviz tahsilatını reddediyor — sandbox'ta ölçüldü,
 * `5184 "Yerli kartlar ile döviz ödemesi yapılamaz"`. USD tutar tahsilat
 * anındaki TCMB döviz satış kuruyla TL'ye çevriliyor.
 *
 * Oran hesabı (yükseltme indirimi) ve yenileme tutarı USD'de kalıyor: plan
 * fiyatı kurla oynamasın, "bu ay neden daha pahalı" sorusunun cevabı fiyat
 * değil kur olsun ve ekranda öyle yazsın.
 *
 * YABANCI KART → USD (15 Eki 2026, iyzico "Multi Currency" eklentisi).
 * `PAY_MULTI_CURRENCY_ENABLED` açıkken Türkiye dışında çıkarılmış kart kurla
 * çevrilmeden doğrudan USD'den çekilir; yerli kart TL'de kalır (5184 kuralı
 * eklentiyle de değişmiyor). Kural `chargeCurrencyFor`da — ekran ve sunucu
 * aynısını çağırıyor. iyzico dövizi reddederse (5184 yerli kart, 5185 döviz
 * POS'u yok) aynı deneme TL'ye düşer; reddedilen başlatma iyzico'da kayıt
 * bırakmıyor (sandbox'ta ölçüldü, `/payment/detail` → 5087).
 */
export const LIST_CURRENCY = 'USD';
export const CHARGE_CURRENCY = 'TRY';
/// Yabancı kartın çekildiği para birimi — katalogla aynı, kur yok.
export const FOREIGN_CHARGE_CURRENCY = LIST_CURRENCY;

/**
 * Kartın çekileceği para birimi. Yalnız BIN'i kesin YABANCI olan kart
 * (`domestic === false`) dövizle çekilir; bilinmeyen kart TL — TL her kartla
 * çalışıyor, döviz yalnız yabancı kartla.
 */
export function chargeCurrencyFor(card: { domestic: boolean | null } | null, multiCurrency: boolean): string {
  return multiCurrency && card?.domestic === false ? FOREIGN_CHARGE_CURRENCY : CHARGE_CURRENCY;
}

/**
 * Kart ekleme doğrulamasında alınan provizyon (TL). Banka 3DS ile kartı
 * doğruluyor, tutar AYNI GÜN iptal ediliyor (iyzico `cancel`) — müşterinin
 * ekstresine geçmez. Sıfır tutarlı doğrulama iyzico'da yok.
 */
export const CARD_VERIFY_AMOUNT = 1;

/// 3DS sayfasına gidip dönmek için süre. Dolunca deneme `EXPIRED` olur ve
/// geç gelen banka dönüşü tahsilata ÇEVRİLMEZ.
export const PAYMENT_ATTEMPT_TTL_MINUTES = 30;

/**
 * Başarısız yenilemeden sonra yeniden deneme aralıkları (gün). İlk deneme
 * dönem sonunda; sonra 1, 3 ve 5 gün arayla üç deneme daha. Dördü de
 * düşerse abonelik `EXPIRED` olur — toplam ~9 günlük tolerans.
 *
 * Bu sürede ürün KAPANMIYOR (`PAST_DUE` erişimi kesmiyor): çoğu red geçici
 * (limit doldu, banka sistemi) ve ilk denemede kapatmak kartını yenilemiş
 * kullanıcıyı cezalandırmak olurdu.
 */
export const RENEWAL_RETRY_DAYS = [1, 3, 5] as const;

/// Yenilemeden kaç gün önce "kartınızın süresi doluyor" uyarısı verilir.
export const CARD_EXPIRY_WARN_DAYS = 30;

// ═══════════════════════════════════════════════════════════════════════════
// Kart yardımcıları — istemci ve sunucu AYNI kuralı uygular
// ═══════════════════════════════════════════════════════════════════════════

/// Kart numarasından rakam dışı her şeyi atar (boşluk, tire).
export function cardDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * Luhn (mod 10) — kart numarasının yazım hatası kontrolü.
 *
 * Sunucuya gitmeden yakalanan hata, bankaya giden ve REDDEDİLEN bir işlemden
 * ucuzdur: reddedilen işlemler bankanın dolandırıcılık skorunu da etkiliyor.
 */
export function luhnValid(digits: string): boolean {
  if (!/^\d{12,19}$/.test(digits)) return false;
  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return sum % 10 === 0;
}

export const CARD_ASSOCIATIONS = ['VISA', 'MASTER_CARD', 'AMERICAN_EXPRESS', 'TROY'] as const;
export type CardAssociation = (typeof CARD_ASSOCIATIONS)[number];

/**
 * İlk hanelerden kart markası — yazarken logoyu göstermek için.
 *
 * Kesin cevap iyzico'nun BIN sorgusunda (`POST /billing/cards/bin`); bu
 * yalnız ilk tuşta tahmin. İkisi çelişirse ekran sunucuyu dinler.
 */
export function detectCardAssociation(digits: string): CardAssociation | null {
  if (/^4/.test(digits)) return 'VISA';
  if (/^3[47]/.test(digits)) return 'AMERICAN_EXPRESS';
  // Troy (Türkiye yerel şeması) 9792 ile başlıyor.
  if (/^9792/.test(digits)) return 'TROY';
  if (/^5[1-5]/.test(digits)) return 'MASTER_CARD';
  // Mastercard 2-serisi: 2221–2720.
  const two = Number(digits.slice(0, 4));
  if (digits.length >= 4 && two >= 2221 && two <= 2720) return 'MASTER_CARD';
  return null;
}

/// CVC uzunluğu: American Express 4, diğerleri 3.
export function cvcLength(association: CardAssociation | null): 3 | 4 {
  return association === 'AMERICAN_EXPRESS' ? 4 : 3;
}

/**
 * İki haneli yılı dört haneye çevirir (`29` → `2029`). Formda "AA/YY"
 * yazılıyor; sağlayıcı ve veritabanı dört haneli yıl istiyor.
 */
export function normalizeExpiryYear(year: number): number {
  return year < 100 ? 2000 + year : year;
}

/// Son kullanma ayının SONUNA kadar geçerli — ayın ilk günü değil.
export function cardExpired(month: number, year: number, now: Date = new Date()): boolean {
  const y = normalizeExpiryYear(year);
  const endOfMonth = Date.UTC(y, month, 1); // bir sonraki ayın ilk anı
  return now.getTime() >= endOfMonth;
}

// ═══════════════════════════════════════════════════════════════════════════
// Kart girişi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yeni kart. 🔴 Mesajlar DEĞER İÇERMEZ — bir doğrulama hatası loglanırsa
 * kart numarası log satırına düşmesin.
 */
export const cardInputSchema = z
  .object({
    holderName: z
      .string()
      .trim()
      .min(3, 'Kart üzerindeki ad en az 3 karakter')
      .max(60, 'Kart üzerindeki ad en fazla 60 karakter')
      // Harf, boşluk, nokta, kesme, tire. Rakam yok — kart numarasının yanlış
      // kutuya yapıştırılması en sık hata ve o numara buradan geçmemeli.
      .regex(/^[\p{L}][\p{L} .'-]*$/u, 'Kart üzerindeki ad yalnız harf içerebilir'),
    number: z
      .string()
      .max(40)
      .transform(cardDigits)
      .refine(luhnValid, 'Kart numarası geçersiz'),
    expireMonth: z.coerce.number().int().min(1, 'Ay geçersiz').max(12, 'Ay geçersiz'),
    expireYear: z.coerce
      .number()
      .int()
      .transform(normalizeExpiryYear)
      .refine((year) => year >= 2000 && year <= 2100, 'Yıl geçersiz'),
    cvc: z.string().regex(/^\d{3,4}$/, 'Güvenlik kodu 3 ya da 4 hane'),
  })
  .refine((card) => !cardExpired(card.expireMonth, card.expireYear), {
    message: 'Kartın son kullanma tarihi geçmiş',
    path: ['expireYear'],
  })
  .refine(
    (card) => card.cvc.length === cvcLength(detectCardAssociation(card.number)),
    { message: 'Güvenlik kodu uzunluğu kartla uyuşmuyor', path: ['cvc'] },
  );
export type CardInput = z.infer<typeof cardInputSchema>;

/**
 * Ödeme yöntemi: kayıtlı kart ya da yeni kart.
 *
 * `save` yeni kartın saklanıp saklanmayacağı. Abonelikte sunucu bunu ZORLA
 * `true` yapıyor: otomatik yenileme kayıtlı kart olmadan çalışamaz ve ekran
 * bunu kutunun yanında açıkça yazıyor.
 */
export const paymentMethodSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('saved'), cardId: z.uuid() }),
  z.object({
    type: z.literal('new'),
    card: cardInputSchema,
    save: z.boolean().default(false),
    alias: z.string().trim().max(40).optional(),
  }),
]);
export type PaymentMethodInput = z.infer<typeof paymentMethodSchema>;

/// Açık yönlendirme koruması — yalnız `/dashboard` altı.
const returnToSchema = z
  .string()
  .max(200)
  .regex(/^\/dashboard(\/[\w\-/[\]]*)?$/)
  .default('/dashboard/settings/billing');

export const payablePlanSchema = z.enum(['STARTER', 'GROWTH', 'PRO']);
export type PayablePlan = z.infer<typeof payablePlanSchema>;
export const billingIntervalInputSchema = z.enum(['monthly', 'annual']);
export type BillingIntervalInput = z.infer<typeof billingIntervalInputSchema>;

/**
 * Ne satın alınıyor. Tutar YOK — tutar her zaman sunucuda katalogdan
 * hesaplanıyor.
 *
 * `renewal`: gecikmiş (`PAST_DUE`) aboneliğin yenilemesini kullanıcının
 * şimdi, isterse yeni bir kartla ödemesi.
 */
export const payTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('subscription'),
    plan: payablePlanSchema,
    interval: billingIntervalInputSchema,
  }),
  z.object({
    kind: z.literal('topup'),
    packId: creditPackIdSchema,
  }),
  z.object({ kind: z.literal('renewal') }),
]);
export type PayTarget = z.infer<typeof payTargetSchema>;

export const payQuoteRequestSchema = z.object({ target: payTargetSchema });
export type PayQuoteRequest = z.infer<typeof payQuoteRequestSchema>;

export const payRequestSchema = z.object({
  target: payTargetSchema,
  /**
   * Ödeme yöntemi. Opsiyonel çünkü düşürmede (dönem sonuna planlanan
   * değişiklik) ödeme YOK ve kart istenmiyor. Şimdi çekim gereken hedefte
   * eksikse sunucu 400 döner — hangi hedefin ödeme gerektirdiğini yalnız
   * sunucu biliyor (oran hesabı).
   */
  method: paymentMethodSchema.optional(),
  /**
   * Mesafeli satış sözleşmesi + (abonelikte) otomatik yenileme onayı.
   * `true` dışında bir değer 400: tekrarlayan tahsilat açık onay istiyor.
   */
  acceptTerms: z.literal(true),
  returnTo: returnToSchema,
});
export type PayRequest = z.infer<typeof payRequestSchema>;

export const addCardRequestSchema = z.object({
  card: cardInputSchema,
  alias: z.string().trim().max(40).optional(),
  /// Otomatik yenilemenin bu karttan çekilmesi.
  makeDefault: z.boolean().default(true),
  returnTo: returnToSchema,
});
export type AddCardRequest = z.infer<typeof addCardRequestSchema>;

/// BIN sorgusu: ilk 6–8 hane. Tam numara GÖNDERİLMEZ.
export const binCheckRequestSchema = z.object({
  bin: z.string().regex(/^\d{6,8}$/),
});
export type BinCheckRequest = z.infer<typeof binCheckRequestSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıtlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ödeme özeti — "Öde" düğmesinden ÖNCE kullanıcının gördüğü rakamlar.
 *
 * `action`:
 *   new        → planı yok / deneme / bitmiş plan: tam fiyat, dönem bugün başlar
 *   upgrade    → üst kademe ya da aylıktan yıllığa: kalan gün değeri düşülür,
 *                yeni dönem bugün başlar
 *   downgrade  → alt kademe ya da yıllıktan aylığa: ÖDEME YOK, dönem sonunda
 *                geçilir (parası ödenmiş dönem sonuna kadar kullanılır)
 *   renewal    → gecikmiş yenilemeyi şimdi ödemek
 *   topup      → kredi paketi
 */
export interface PayQuoteResponse {
  action: 'new' | 'upgrade' | 'downgrade' | 'renewal' | 'topup';
  /// `now` = şimdi tahsil edilir; `period_end` = hiçbir şey çekilmez, değişiklik planlanır.
  effect: 'now' | 'period_end';
  plan: PlanTier | null;
  interval: BillingIntervalInput | null;
  packId: string | null;
  credits: number | null;
  /// Aşağıdaki tutarların para birimi — KATALOG (USD).
  currency: string;
  /// Liste fiyatı.
  listAmount: number;
  /// Kullanılmayan günlerin değeri (yalnız yükseltmede > 0).
  prorationCredit: number;
  /// Şimdi çekilecek tutar. `period_end`de 0.
  dueNow: number;
  /// Yeni ödeme döneminin sınırları; paket ve planlanmış değişiklikte null.
  periodStart: string | null;
  periodEnd: string | null;
  /// Otomatik yenilemenin tarihi ve tutarı (abonelikte).
  renewsAt: string | null;
  renewalAmount: number | null;
  /// Bu satın almada kart KAYDI zorunlu mu (abonelik: evet, paket: hayır).
  cardRequired: boolean;
  /**
   * KARTTAN ÇEKİLECEK tutar — `dueNow`ın TL karşılığı, bugünkü kurla.
   * `period_end`de null (çekim yok). Ödeme başlatıldığında kur yeniden
   * okunuyor; TCMB günde bir yayımladığı için pratikte aynı rakam.
   */
  charge: {
    amount: number;
    currency: string;
    /// 1 USD kaç TL.
    rate: number;
    /// Kurun TCMB yayın tarihi (YYYY-MM-DD).
    rateDate: string;
  } | null;
  /**
   * Türkiye dışında çıkarılmış kartla ödenirse çekilecek tutar — `dueNow`ın
   * kendisi, USD, kur yok. `null` = çoklu para birimi kapalı ya da çekim yok;
   * o zaman her kart `charge`la öder. Hangisinin geçerli olduğu kartın
   * BIN'ine bağlı (`chargeCurrencyFor`).
   */
  foreignCharge: {
    amount: number;
    currency: string;
  } | null;
}

export type PayStartResponse =
  | {
      /// Tarayıcı bu adrese TAM SAYFA gider — bankanın 3DS ekranı.
      status: 'redirect';
      attemptId: string;
      redirectUrl: string;
    }
  | {
      /// Ödeme yok; değişiklik dönem sonuna planlandı.
      status: 'scheduled';
      effectiveAt: string;
      plan: PlanTier;
      interval: BillingIntervalInput;
    };

export const PAYMENT_ATTEMPT_STATUSES = [
  'PENDING',
  'PROCESSING',
  'REVIEW',
  'SUCCEEDED',
  'FAILED',
  'EXPIRED',
] as const;
export type PaymentAttemptStatusValue = (typeof PAYMENT_ATTEMPT_STATUSES)[number];

export const PAYMENT_PURPOSES = ['SUBSCRIPTION', 'RENEWAL', 'TOPUP', 'CARD_VERIFY'] as const;
export type PaymentPurposeValue = (typeof PAYMENT_PURPOSES)[number];

/**
 * Sınıflandırılmış başarısızlık sebebi — ekran bunu çeviriyor.
 *
 * 🔴 Bankanın ham mesajı ekrana GİTMEZ: kart/kimlik parçası taşıyabiliyor ve
 * Türkçe/İngilizce karışık. Sağlayıcı kodu (`10051` gibi) bu listeye
 * `iyzico-errors.ts`te eşleniyor; bilinmeyen kod `CARD_DECLINED`a düşüyor.
 */
export const PAYMENT_FAILURE_CODES = [
  /// Banka reddetti, sebep söylemedi ("do not honour").
  'CARD_DECLINED',
  'INSUFFICIENT_FUNDS',
  'EXPIRED_CARD',
  'INVALID_CVC',
  'INVALID_CARD',
  /// Kart internet/yurt dışı/döviz işlemine kapalı.
  'CARD_NOT_PERMITTED',
  /// Kayıp/çalıntı ya da dolandırıcılık şüphesi. Kullanıcıya genel mesaj.
  'CARD_BLOCKED',
  'FRAUD_SUSPECTED',
  /// Bankanın 3DS doğrulaması geçmedi (yanlış SMS kodu, iptal).
  'THREEDS_FAILED',
  /// Kart 3DS'e kayıtlı değil / banka 3DS desteklemiyor.
  'THREEDS_UNAVAILABLE',
  /// Banka bu kartla 3DS'siz (otomatik) tahsilata izin vermiyor — çoğunlukla
  /// banka kartları. Aynı kart 3D Secure ile elle ödenebilir.
  'THREEDS_REQUIRED',
  /// 3DS süresinde tamamlanmadı ya da yeni bir denemeyle geçersiz kılındı.
  'EXPIRED',
  'SUPERSEDED',
  /// Çekilen tutar beklenenle uyuşmadı — işlem iptal edildi.
  'AMOUNT_MISMATCH',
  /// Otomatik yenilemede kayıtlı kart yok ya da doğrulanmamış.
  'NO_CARD',
  /// Sağlayıcıya ulaşılamadı / beklenmeyen yanıt.
  'PROVIDER_ERROR',
] as const;
export type PaymentFailureCode = (typeof PAYMENT_FAILURE_CODES)[number];

export interface PaymentAttemptView {
  id: string;
  purpose: PaymentPurposeValue;
  status: PaymentAttemptStatusValue;
  failureCode: PaymentFailureCode | null;
  plan: PlanTier | null;
  interval: BillingIntervalInput | null;
  packId: string | null;
  credits: number | null;
  amount: number;
  currency: string;
  card: { lastFour: string; association: string | null } | null;
  createdAt: string;
  completedAt: string | null;
}

export interface PaymentCardRow {
  id: string;
  alias: string | null;
  lastFour: string;
  association: CardAssociation | string | null;
  cardType: string | null;
  family: string | null;
  bankName: string | null;
  expireMonth: number | null;
  expireYear: number | null;
  isDefault: boolean;
  verified: boolean;
  /// Son kullanma tarihi geçmiş.
  expired: boolean;
  /// Bir sonraki yenilemeden önce süresi dolacak.
  expiresBeforeRenewal: boolean;
  /**
   * TEST KARTI (`PAY_TEST_CARD_ENABLED`, GEÇİCİ — 1 Eki 2026). Jetonu sahte
   * (`TEST-…`), iyzico'da karşılığı yok; bu kartla yapılan her tahsilat
   * simüle edilir, para çekilmez. Ekran "Test kartı" rozetiyle gösterir.
   */
  test: boolean;
  /**
   * Kart HİÇBİR tahsilatta kullanılamaz (ödeme, varsayılan, otomatik
   * yenileme). Bugün tek sebebi: test kartı ve test modu kapatılmış. Ekran
   * kartı geçersiz gösterir ve ödeme seçeneklerine koymaz; silinebilir.
   */
  invalid: boolean;
  /// Kart Türkiye'de mi çıkarılmış (`BinCheckResponse.domestic`). Çoklu para
  /// birimi kapalıyken sorulmuyor: `null`.
  domestic: boolean | null;
  createdAt: string;
}

export interface BinCheckResponse {
  association: string | null;
  cardType: string | null;
  family: string | null;
  bankName: string | null;
  /// Kurumsal/ticari kart.
  commercial: boolean;
  /**
   * Türkiye'de çıkarılmış kart mı. iyzico'nun BIN tablosu yerli bankaları
   * tutuyor: yabancı BIN canlıda `5066 Bin bulunamadı`, sandbox'ta
   * "International Bank" (banka kodu 1000) dönüyor — ikisi de `false`.
   * `null` = sağlayıcı başka bir sebeple cevap vermedi, bilinmiyor.
   */
  domestic: boolean | null;
}
